import { useState } from "react";
import { LANDING_URL } from "../../env";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import AuthLayout from "../../components/Auth/AuthLayout";
import LanguageToggle from "../../components/Common/LanguageToggle";
import Button from "../../components/Common/Button";
import ErrorMessage from "../../components/Common/ErrorMessage";
import PasswordInput from "../../components/Common/PasswordInput";
import { resetPassword as resetPasswordRequest } from "../../api/authApi";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { showFirstFormErrorToast } from "../../utils/formErrorToast";
import { showErrorToast } from "../../utils/toast";

const ResetPassword = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const email = searchParams.get("email") || "";
    const token = searchParams.get("token") || "";

    useState(() => {
        if (email || token) {
            window.history.replaceState(null, "", "/reset-password");
        }
    });
    const navigate = useNavigate();
    const [submitted, setSubmitted] = useState(false);
    const { run, isLoading, clearError } = useAsyncAction();
    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            password: "",
            confirmPassword: "",
        },
    });

    const passwordValue = watch("password");

    const onSubmit = async (values) => {
        clearError();
        const result = await run(
            () => resetPasswordRequest({
                email,
                token,
                password: values.password,
                confirmPassword: values.confirmPassword,
            }),
            { errorFallback: t("AUTH_RESET_PASSWORD_ERROR_RESET") }
        );

        if (result?.success === true || result?.success === 1) {
            setSubmitted(true);
        } else if (result?.success === false || result?.success === 0) {
            showErrorToast(result?.message || t("AUTH_RESET_PASSWORD_ERROR_RESET"));
        }
    };

    const handleFormInvalid = (formErrors) => {
        showFirstFormErrorToast(formErrors);
    };

    return (
        <>
        <div className="fixed top-6 right-6 z-50">
            <LanguageToggle />
        </div>
        <AuthLayout
            left={(
                <div className="order-2 flex flex-col justify-center px-6 py-10 sm:px-10 lg:order-1 lg:px-16">
                    <div className="flex items-center gap-3 text-sm font-semibold tracking-[0.2em] text-slate-700">
                        <a href={LANDING_URL} className="hover:opacity-75 transition-opacity">
                            <img
                                src="/assets/icons/korres-shares-logo.png"
                                alt={t("AUTH_RESET_PASSWORD_ALT_KORRES_SHARES")}
                                className="h-[4.5rem] w-[4.5rem]"
                            />
                        </a>
                    </div>

                    <div className="mt-8 max-w-md">
                        {submitted ? (
                            <>
                                <h1 className="text-2xl font-semibold text-slate-900">
                                    {t("AUTH_RESET_PASSWORD_SUCCESS_TITLE")}
                                </h1>
                                <p className="mt-2 text-sm text-slate-500">
                                    {t("AUTH_RESET_PASSWORD_SUCCESS_SUBTITLE")}
                                </p>
                                <div className="mt-8">
                                    <Button
                                        type="button"
                                        className="w-full"
                                        onClick={() => navigate("/login")}
                                    >
                                        {t("COMMON_BACK_TO_SIGN_IN")}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h1 className="text-2xl font-semibold text-slate-900">
                                    {t("AUTH_RESET_PASSWORD_TITLE")}
                                </h1>
                                <p className="mt-2 text-sm text-slate-500">
                                    {t("AUTH_RESET_PASSWORD_SUBTITLE")}
                                </p>
                                <form className="mt-8 space-y-4" onSubmit={handleSubmit(onSubmit, handleFormInvalid)}>
                                    <div>
                                        <Controller
                                            name="password"
                                            control={control}
                                            rules={{
                                                required: t("AUTH_VALIDATION_PASSWORD_REQUIRED"),
                                                minLength: {
                                                    value: 6,
                                                    message: t("AUTH_VALIDATION_PASSWORD_MIN_LENGTH"),
                                                },
                                            }}
                                            render={({ field }) => (
                                                <PasswordInput
                                                    {...field}
                                                    placeholder={t("AUTH_RESET_PASSWORD_PLACEHOLDER_NEW_PASSWORD")}
                                                />
                                            )}
                                        />
                                        <ErrorMessage message={errors.password?.message} />
                                    </div>
                                    <div>
                                        <Controller
                                            name="confirmPassword"
                                            control={control}
                                            rules={{
                                                required: t("AUTH_VALIDATION_CONFIRM_PASSWORD_REQUIRED"),
                                                validate: (value) =>
                                                    value === passwordValue || t("AUTH_VALIDATION_PASSWORDS_NOT_MATCH"),
                                            }}
                                            render={({ field }) => (
                                                <PasswordInput
                                                    {...field}
                                                    placeholder={t("AUTH_RESET_PASSWORD_PLACEHOLDER_CONFIRM_PASSWORD")}
                                                />
                                            )}
                                        />
                                        <ErrorMessage message={errors.confirmPassword?.message} />
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={!isValid || isLoading}
                                        className="mt-2 w-full"
                                    >
                                        {isLoading ? t("COMMON_SUBMITTING") : t("COMMON_SUBMIT")}
                                    </Button>
                                </form>

                            </>
                        )}
                    </div>
                </div>
            )}
            right={(
                <div className="relative order-1 w-full overflow-hidden bg-cream lg:order-2 lg:h-screen">
                    <img
                        src="/assets/images/common/create-password.png"
                        alt=""
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
        />
        </>
    );
};

export default ResetPassword;
